import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { ToastContext, type Toast, type ToastContextValue, type ToastSeverity, type ToastVariant } from './toastContextCore'

const AUTO_DISMISS_MS = 3_200
// If the same (message, variant) fires within this window we treat it as the
// same event. Stops runaway loops (a broken effect firing on every re-render,
// a callable that keeps returning INTERNAL) from stacking a wall of toasts.
const DEDUPE_WINDOW_MS = 2_500

const newToastId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `toast-${performance.now()}-${Math.round(Math.random() * 1e6)}`

// Rewrite low-signal wire messages before they hit the user. Firebase Callables
// surface any unhandled server error as the literal string "INTERNAL", which is
// scary and useless. Prefer a human-friendly line so the toast reads like part
// of the app, not a raw error code.
const normalizeMessage = (raw: string): string => {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return 'Something went wrong. Please try again.'
  if (/^internal$/i.test(trimmed)) return 'Something went wrong. Please try again.'
  if (/^unavailable$/i.test(trimmed)) return 'Service is temporarily unavailable. Please try again.'
  if (/^unauthenticated$/i.test(trimmed)) return 'You need to sign in again to continue.'
  if (/^permission-denied$/i.test(trimmed)) return "You don't have permission to do that."
  return trimmed
}

export const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [paused, setPaused] = useState(false)
  const [focusModeActive, setFocusModeActiveState] = useState(false)
  const pausedRef = useRef(false)
  const queuedToasts = useRef<Toast[]>([])
  const timers = useRef(new Map<string, number>())
  const lastSignatures = useRef(new Map<string, number>())

  const setFocusModeActive = useCallback((active: boolean) => {
    setFocusModeActiveState(Boolean(active))
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    queuedToasts.current = queuedToasts.current.filter((toast) => toast.id !== id)
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const showToast = useCallback(
    (
      message: string,
      variant: ToastVariant = 'info',
      action?: Toast['action'],
      severity: ToastSeverity = 'normal',
      options?: Pick<Toast, 'durationMs'>,
    ) => {
      const normalized = normalizeMessage(message)
      const signature = `${variant}:${normalized}`
      const now = performance.now()
      const lastAt = lastSignatures.current.get(signature)
      if (lastAt !== undefined && now - lastAt < DEDUPE_WINDOW_MS) {
        lastSignatures.current.set(signature, now)
        // Return the newest matching id so the caller still has something
        // to dismiss; if none is visible any longer we synthesise one.
        const existingToasts = [...toasts, ...queuedToasts.current]
        for (let i = existingToasts.length - 1; i >= 0; i -= 1) {
          const existing = existingToasts[i]
          if (existing.variant === variant && existing.message === normalized) {
            return existing.id
          }
        }
      }
      lastSignatures.current.set(signature, now)
      const id = newToastId()
      const toast: Toast = { id, message: normalized, variant, action, severity, durationMs: options?.durationMs }
      if (pausedRef.current && severity !== 'critical') {
        queuedToasts.current.push(toast)
        return id
      }
      setToasts((current) => [...current, toast])
      const timer = window.setTimeout(() => dismissToast(id), toast.durationMs ?? AUTO_DISMISS_MS)
      timers.current.set(id, timer)
      return id
    },
    [dismissToast, toasts],
  )

  const pauseToasts = useCallback(() => {
    pausedRef.current = true
    setPaused(true)
  }, [])

  const resumeToasts = useCallback(() => {
    pausedRef.current = false
    setPaused(false)
    if (queuedToasts.current.length === 0) return
    const queued = queuedToasts.current
    queuedToasts.current = []
    setToasts((current) => [...current, ...queued])
    for (const toast of queued) {
      timers.current.set(toast.id, window.setTimeout(() => dismissToast(toast.id), toast.durationMs ?? AUTO_DISMISS_MS))
    }
  }, [dismissToast])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleToastEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (!detail || typeof detail.message !== 'string') return
      showToast(detail.message, detail.variant, detail.action, detail.severity, detail.options)
    }
    window.addEventListener('timelining:show-toast', handleToastEvent)
    return () => window.removeEventListener('timelining:show-toast', handleToastEvent)
  }, [showToast])

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, paused, focusModeActive, showToast, dismissToast, pauseToasts, resumeToasts, setFocusModeActive }),
    [toasts, paused, focusModeActive, showToast, dismissToast, pauseToasts, resumeToasts, setFocusModeActive],
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const emitToast = (
  message: string,
  variant: ToastVariant = 'info',
  action?: Toast['action'],
  severity: ToastSeverity = 'normal',
  options?: Pick<Toast, 'durationMs'>,
) => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('timelining:show-toast', {
        detail: { message, variant, action, severity, options },
      }),
    )
  }
}
